import React, { useEffect, useMemo, useCallback, useState } from 'react'
import AreaChart from '../../components/Charts/AreaChart'
import { useDispatch, useSelector } from 'react-redux'
import { useSwitch, useSelect } from '@chilecompra/react-kit'
import { Card, DataTable, Select, Switch, Typography } from '@chilecompra/react-kit/components'
import { onGetSmallerCompaniesComparedThunk } from './SmallerCompaniesCompared.actions'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import InfoEMTDialog from '../../components/Dialogs/InfoEMTDialog'
import {
  DialogLink,
  CardCustom,
  Content,
  Description,
  ExclamationIcon,
  Info,
  InfoCircle,
  GridContainer,
  GridItem,
  Root,
  Title
} from './SmallerCompaniesCompared.styles'
import { INDICATORS_DICTIONARY, INDICATORS_OPTIONS, OPTION_ALL } from '../../config/settings/constants'
import InfoSmallerCompaniesCompared from '../../components/Infographics/InfoSmallerCompaniesCompared'

/**
 * The SmallerCompaniesCompared's container.
 */
const SmallerCompaniesCompared = () => {
  const dispatch = useDispatch()
  const { latestUpdate, loading, sectors: sectorOptions = [], startYear } = useSelector(state => state.topNavbar)
  const { data, csv, series } = useSelector(state => state.smallerCompaniesCompared)
  const [helpOpen, setHelpOpen] = useState(false)
  const handleCloseHelp = useCallback(() => setHelpOpen(false), [])
  const handleOpenHelp = useCallback(() => setHelpOpen(true), [])

  const { value: indicatorValue, onChange: handleChangeIndicator } = useSelect({
    initialValue: INDICATORS_DICTIONARY.amount
  })

  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()

  const { value: sectorValue, onChange: handleChangeSector } = useSelect({
    initialValue: OPTION_ALL,
    changeCallback: updatedValue => {
      dispatch(onGetSmallerCompaniesComparedThunk({ sector: updatedValue }))
    },
    overwriteCallback: () => OPTION_ALL
  })
  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv],
    captureImageCallback: () => exportToImage('smaller-companies-compared-chart', 'empresas-menor-tamano-comparadas'),
    downloadFileCallback: () => exportToCsv(csv, 'Cifras_Mensuales_EMT')
  })

  const categories = useMemo(() => data.map(d => d.year), [data])

  const handleTooltipFormatter = useCallback(
    function () {
      const year = this.x

      let total = 0
      let emt = 0

      this.points.forEach(point => {
        if (point.series.name === 'Total Transado') {
          total = formatAmount(point.y)
        }
        if (point.series.name === 'Empresas de Menor Tamaño') {
          emt = formatAmount(point.y)
        }
      })

      const isEmptyCompanies = total === 0
      const isEmptyEmt = emt === 0

      if (indicatorValue === INDICATORS_DICTIONARY.amount) {
        total = currencyValue ? `USD ${total}` : `$${total}`
        emt = currencyValue ? `USD ${emt}` : `$${emt}`
      }

      let textCompanies = ''

      if (!isEmptyCompanies) {
        textCompanies = `<strong>${total}</strong> transados durante el año <strong>${year}</strong>.`

        if (!isEmptyEmt) {
          textCompanies += `<br/>Versus `
        }
      }
      const textEmt = isEmptyEmt
        ? ''
        : `<strong>${emt}</strong> transados a proveedores <br/>de Empresas de menor tamaño.`

      return `
        <span style="font-size:12px;">
          ${textCompanies}
          ${textEmt}
        </span>
      `
    },
    [currencyValue, data, indicatorValue]
  )
  const processedData = useMemo(() => {
    return series.map(datum => {
      let valueArray = []

      if (indicatorValue === INDICATORS_DICTIONARY.amount) {
        valueArray = currencyValue ? datum.amountUSD : datum.amount
      } else if (indicatorValue === INDICATORS_DICTIONARY.orders) {
        valueArray = datum.orders
      }

      return {
        ...datum,
        value: valueArray,
        data: valueArray
      }
    })
  }, [currencyValue, indicatorValue, series])

  let headerDef
  let recordsDecoratorsDef
  if (indicatorValue === INDICATORS_DICTIONARY.amount) {
    headerDef = [
      { label: 'Año', value: 'year' },
      { label: 'Micro', value: currencyValue ? 'amountMicro' : 'amountUSDMicro' },
      { label: 'Pequeña', value: currencyValue ? 'amountSmaller' : 'amountUSDSmaller' },
      { label: 'Mediana', value: currencyValue ? 'amountMedium' : 'amountUSDMedium' },
      { label: 'Total EMT', value: currencyValue ? 'amountCLPTotalEMT' : 'amountUSDTotalEMT' },
      { label: '% EMT respecto del total', value: 'percentageAmountTotalEMT' },
      { label: 'Total general', value: currencyValue ? 'amountCLPTotalCompanies' : 'amountUSDTotalCompanies' }
    ]
    recordsDecoratorsDef = [
      d => d.year,
      d => (currencyValue ? `USD ${formatAmount(d.amountUSDMicro)}` : `$${formatAmount(d.amountMicro)}`),
      d => (currencyValue ? `USD ${formatAmount(d.amountUSDSmaller)}` : `$${formatAmount(d.amountSmaller)}`),
      d => (currencyValue ? `USD ${formatAmount(d.amountUSDMedium)}` : `$${formatAmount(d.amountMedium)}`),
      d => (currencyValue ? `USD ${formatAmount(d.amountUSDTotalEMT)}` : `$${formatAmount(d.amountCLPTotalEMT)}`),
      d => d.percentageAmountTotalEMT,
      d =>
        currencyValue ? `USD ${formatAmount(d.amountUSDTotalCompanies)}` : `$${formatAmount(d.amountCLPTotalCompanies)}`
    ]
  } else if (indicatorValue === INDICATORS_DICTIONARY.orders) {
    headerDef = [
      { label: 'Año', value: 'year' },
      { label: 'Micro', value: 'ordersMicro' },
      { label: 'Pequeña', value: 'ordersSmaller' },
      { label: 'Mediana', value: 'ordersMediana' },
      { label: 'Total EMT', value: 'ordersEMTTotal' },
      { label: '% EMT respecto del total', value: 'percentageOCTotalEMT' },
      { label: 'Total general', value: 'ordersTotal' }
    ]
    recordsDecoratorsDef = [
      d => d.year,
      d => formatAmount(d.ordersMicro),
      d => formatAmount(d.ordersSmaller),
      d => formatAmount(d.ordersMediana),
      d => formatAmount(d.ordersEMTTotal),
      d => formatAmount(d.percentageOCTotalEMT),
      d => formatAmount(d.ordersTotal)
    ]
  }

  useEffect(() => {
    if (!OPTION_ALL) return
    dispatch(onGetSmallerCompaniesComparedThunk({ sector: sectorValue }))
  }, [OPTION_ALL])
  return (
    <Root>
      <Content>
        <Typography
          color="primary"
          fontSize="18px"
          fontWeight="bold"
          lineHeight="21px"
          margin="0 0 24px"
          variant="body1"
        >
          Porcentaje y montos transados
        </Typography>
        <GridContainer>
          <GridItem xs={6}>
            <Title>Empresas de menor tamaño comparadas con el monto total transado</Title>
          </GridItem>
        </GridContainer>
        <GridContainer justifyContent="space-between" spacing={2}>
          <GridItem xs={6}>
            <Description>
              Visualiza la información de las compras realizadas a las Empresas de menor tamaño en comparación con el
              monto total transado dentro del año vigente y años anteriores.
            </Description>
          </GridItem>
          <GridItem xs={5}>
            <CardCustom color="primary">
              <GridItem xs={1} padding="10px 0 0 0">
                <Info>
                  <InfoCircle>
                    <ExclamationIcon />
                  </InfoCircle>
                </Info>
              </GridItem>
              <GridItem xs={11} padding="10px 4px">
                <GridItem xs={12}>
                  <Typography fontSize="14px" lineHeight="16px" variant="body1">
                    El tamaño del proveedor es información provista por el SII. <br />
                    <DialogLink onClick={handleOpenHelp}>Ver información completa</DialogLink>
                  </Typography>
                </GridItem>
              </GridItem>
            </CardCustom>
          </GridItem>
        </GridContainer>

        <DataLayout
          disableCaptureImage={disableCaptureImage}
          disableDownloadFile={disableDownloadFile}
          header={
            <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
              <GridItem md={10} xs={12}>
                <GridContainer alignItems="center" spacing={1}>
                  <GridItem md={3} xs={12}>
                    <Select
                      label="indicator"
                      loading={loading}
                      options={INDICATORS_OPTIONS}
                      value={indicatorValue}
                      onChange={handleChangeIndicator}
                    />
                  </GridItem>
                  <GridItem md={3} xs={12}>
                    <Select
                      label="Sector del estado"
                      loading={loading}
                      options={sectorOptions}
                      value={sectorValue}
                      onChange={handleChangeSector}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>

              <GridItem md={2} xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Switch
                  checked={currencyValue}
                  color="primary"
                  labelLeft="CLP"
                  labelRight="USD"
                  onChange={handleChangeCurrency}
                  margin="0 0 0 32px"
                  disabled={INDICATORS_DICTIONARY.orders === indicatorValue}
                />
              </GridItem>
            </GridContainer>
          }
          infographic={<InfoSmallerCompaniesCompared />}
          noData={!loading && data?.length === 0}
          noDataText="El organismo comprador no cuenta con información relacionada a sus proveedores durante el año seleccionado."
          ref={dataLayoutRef}
          updatedDate={latestUpdate}
          onCaptureImage={handleCaptureImage}
          onChangeView={handleChangeView}
          onDownloadFile={handleDownloadFile}
          paddingFooter="10px"
        >
          <DataLayoutChild view={DataLayoutViews.CHART}>
            <Card color="blank" elevation="1" margin="0 0 32px">
              <GridContainer padding="0 0 1px">
                <GridItem xs={12}>
                  <AreaChart
                    key={`chart-${currencyValue}-${indicatorValue}`}
                    id="smaller-companies-compared-chart"
                    categories={categories}
                    data={processedData}
                    tooltipFormatter={handleTooltipFormatter}
                    startYear={startYear}
                  />
                </GridItem>
              </GridContainer>
            </Card>
          </DataLayoutChild>

          <DataLayoutChild view={DataLayoutViews.TABLE}>
            <Card color="blank" elevation="1" margin="0 0 32px">
              <GridContainer padding="0 0 20px">
                <GridItem xs={12}>
                  <DataTable
                    headers={headerDef}
                    headersCellsProps={[
                      { enableSort: true, width: '15%' },
                      { enableSort: true },
                      { enableSort: true },
                      { enableSort: true },
                      { enableSort: true },
                      { enableSort: true },
                      { enableSort: true }
                    ]}
                    records={data}
                    loading={loading}
                    recordsDecorators={recordsDecoratorsDef}
                  />
                </GridItem>
              </GridContainer>
            </Card>
          </DataLayoutChild>
        </DataLayout>
        <InfoEMTDialog open={helpOpen} onClose={handleCloseHelp} />
      </Content>
    </Root>
  )
}

export default SmallerCompaniesCompared
